import { useState, useEffect } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { ChevronDown, Calendar, Package, Search, Grid, Tag } from 'lucide-react';
import PermissionGuard from '../components/PermissionGuard';
import PortalDropdown from '../components/ui/PortalDropdown';

// Interface for our item usage data
interface ItemUsage {
  item_id: string;
  item_name: string;
  total_quantity: number;
  total_cost: number;
  unit: string;
  supplier_name: string;
  category_name?: string;
}

// Interface for category data
interface Category {
  id: string;
  name: string;
  item_count: number;
}

// Date range options
type DateRange = 'this-month' | 'last-month' | 'all-time';

export default function SuppliesUsage() {
  const { selectedBusiness } = useBusiness();
  const [itemUsage, setItemUsage] = useState<ItemUsage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('this-month');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Get the human-readable date range label
  const getDateRangeLabel = (range: DateRange): string => {
    switch (range) {
      case 'this-month': return 'This Month';
      case 'last-month': return 'Last Month';
      case 'all-time': return 'All Time';
      default: return 'This Month';
    }
  };

  // Function to fetch stock history data for received items
  const fetchStockUsage = async () => {
    if (!supabase) {
      setIsLoading(false);
      setError("Supabase client not initialized");
      return;
    }

    if (!selectedBusiness?.id) {
      setIsLoading(false);
      setError("No business selected");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Set up the base query with business_id filter
      let query = supabase
        .from('stock_history_with_details')
        .select('item_id, item_name, item_unit, quantity_change, price, transaction_type, supplier_name, category_name, created_at, business_id')
        .eq('transaction_type', 'Received')
        .eq('business_id', selectedBusiness.id);

      // Add date range filters
      if (dateRange !== 'all-time') {
        const now = new Date();
        let startDate: Date;
        let endDate: Date;
        
        if (dateRange === 'this-month') {
          // First day of current month
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          // Last day of current month
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        } else { // last-month
          // First day of previous month
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          // Last day of previous month
          endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        }
        
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      // Execute the query
      const { data, error } = await query;

      if (error) {
        console.error('Error fetching stock history:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('No stock history data found');
        setItemUsage([]);
        setCategories([]);
        setIsLoading(false);
        return;
      }

      console.log('Stock history data:', data);

      // Process data to calculate usage by item
      const usageByItem = new Map<string, ItemUsage>();

      data.forEach(item => {
        const itemId = item.item_id;
        const itemName = item.item_name || 'Unknown Item';
        const quantity = Number(item.quantity_change) || 0;
        const price = Number(item.price) || 0;
        const unit = item.item_unit || 'unit';
        const supplierName = item.supplier_name || 'Unknown Supplier';
        const categoryName = item.category_name || 'Uncategorized';
        const cost = quantity * price;

        const itemKey = `${itemId}-${supplierName}`;

        if (usageByItem.has(itemKey)) {
          const existing = usageByItem.get(itemKey)!;
          existing.total_quantity += quantity;
          existing.total_cost += cost;
          usageByItem.set(itemKey, existing);
        } else {
          usageByItem.set(itemKey, {
            item_id: itemId,
            item_name: itemName,
            total_quantity: quantity,
            total_cost: cost,
            unit,
            supplier_name: supplierName,
            category_name: categoryName
          });
        }
      });

      // Convert map to array and sort by total cost (highest first)
      const usageArray = Array.from(usageByItem.values())
        .sort((a, b) => b.total_cost - a.total_cost);

      setItemUsage(usageArray);

      // Extract categories and count items
      const categoryMap = new Map<string, number>();
      
      usageArray.forEach(item => {
        const categoryName = item.category_name || 'Uncategorized';
        categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + 1);
      });
      
      // Convert to category array
      const categoryArray: Category[] = Array.from(categoryMap.entries()).map(([name, count]) => ({
        id: name.toLowerCase().replace(/\s+/g, '-'),
        name,
        item_count: count
      })).sort((a, b) => b.item_count - a.item_count);
      
      setCategories(categoryArray);
    } catch (err) {
      console.error('Error fetching stock usage:', err);
      setError('Failed to load supplies usage data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount or when business/date range changes
  useEffect(() => {
    fetchStockUsage();
  }, [selectedBusiness?.id, dateRange]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Filter items based on search and category
  const filteredItems = itemUsage.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.supplier_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      (item.category_name || 'Uncategorized') === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Calculate total cost of filtered items
  const totalCost = filteredItems.reduce((sum, item) => sum + item.total_cost, 0);

  // Render product card
  const renderProductCard = (item: ItemUsage) => {
    return (
      <div key={`${item.item_id}-${item.supplier_name}`} 
           className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        
        {/* Product Header */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{item.item_name}</h3>
              <p className="text-sm text-gray-600 mt-1">{item.supplier_name}</p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <div className="text-right">
                <div className="text-xl font-semibold text-blue-600">{formatCurrency(item.total_cost)}</div>
                <div className="text-xs text-gray-500">Total Cost</div>
              </div>
            </div>
          </div>
          
          {/* Product Details */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">{item.total_quantity}</div>
                <div className="text-xs text-gray-500">{item.unit}</div>
              </div>
              {item.category_name && (
                <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
                  <Tag className="h-3 w-3 text-blue-600" />
                  <span className="text-xs text-blue-700 font-medium">{item.category_name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PermissionGuard permission="suppliesUsage.pageAccess">
      <div className="min-h-screen bg-gray-50"
           style={{
             touchAction: 'manipulation',
             WebkitTouchCallout: 'none',
             WebkitUserSelect: 'none',
             paddingTop: 'env(safe-area-inset-top)',
             paddingBottom: 'env(safe-area-inset-bottom)',
             paddingLeft: 'env(safe-area-inset-left)',
             paddingRight: 'env(safe-area-inset-right)',
             fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
           }}>
        
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Page Title */}
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <Package className="h-6 w-6 mr-3 text-blue-600" />
                  Supplies Usage Report
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Track and analyze supplies consumption
                </p>
              </div>
              
              {/* Date Range Filter */}
              <PortalDropdown
                isOpen={isDateDropdownOpen}
                onClose={() => setIsDateDropdownOpen(false)}
                align="right"
                trigger={
                  <button
                    onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                    className="w-full sm:w-auto bg-white border border-gray-300 rounded-lg px-4 py-3 
                              text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors 
                              flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Calendar className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <span className="text-gray-900 truncate">{getDateRangeLabel(dateRange)}</span>
                    </div>
                    <ChevronDown 
                      className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isDateDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                }
              >
                <div className="py-2">
                  {[
                    { value: 'this-month', label: 'This Month' },
                    { value: 'last-month', label: 'Last Month' },
                    { value: 'all-time', label: 'All Time' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${
                        dateRange === option.value 
                          ? 'bg-blue-50 text-blue-600' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setDateRange(option.value as DateRange);
                        setIsDateDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </PortalDropdown>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-7xl mx-auto space-y-6">
            
            {/* Search Bar */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search supplies by name or supplier..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Category Tabs & Results Header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              
              {/* Category Navigation */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Grid className="h-5 w-5 mr-2 text-blue-600" />
                    Supplies Catalog
                    <span className="ml-2 text-sm font-normal text-gray-500">({getDateRangeLabel(dateRange)})</span>
                  </h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                    <div className="font-semibold text-blue-700 text-lg">
                      {isLoading ? 'Loading...' : formatCurrency(totalCost)}
                    </div>
                  </div>
                </div>
                
                {/* Category Tabs */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    All Products ({itemUsage.length})
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === category.name
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {category.name} ({category.item_count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="p-6">
                {isLoading ? (
                  <div className="h-60 flex items-center justify-center">
                    <div className="text-center">
                      <Package className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-pulse" />
                      <p className="text-gray-600">Loading supplies...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="h-60 flex items-center justify-center">
                    <div className="text-center">
                      <Package className="h-8 w-8 text-red-500 mx-auto mb-4" />
                      <p className="text-red-600">{error}</p>
                    </div>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="h-60 flex items-center justify-center">
                    <div className="text-center">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        {searchQuery || selectedCategory !== 'all' ? 'No Results Found' : 'No Data Available'}
                      </h4>
                      <p className="text-gray-500 max-w-sm mx-auto">
                        {searchQuery || selectedCategory !== 'all' 
                          ? 'Try adjusting your search or category filter.'
                          : 'No supplies usage data found for the selected period. Try selecting a different date range.'
                        }
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Results Count */}
                    <div className="mb-6">
                      <p className="text-sm text-gray-600">
                        Showing {filteredItems.length} of {itemUsage.length} products
                        {searchQuery && ` matching "${searchQuery}"`}
                        {selectedCategory !== 'all' && ` in ${selectedCategory}`}
                      </p>
                    </div>
                    
                    {/* Product Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                      {filteredItems.map(renderProductCard)}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
} 